// src/lib/supabase/clubs-utils.ts

import { supabase } from '../supabase';

export interface ClubBadge {
  id: string;          // internal club_id
  name: string;        // club_name
  logo_url: string;    // logo
  is_primary?: boolean;
}

/**
 * Fetch club badges for a user given their Supabase auth_id.
 * 1. Look up the internal users.id by auth_id.
 * 2. Query user_clubs WHERE user_id = that internal ID.
 * 3. Return each club’s { id, name, logo_url, is_primary }.
 */
export async function getUserClubBadges(authId: string): Promise<ClubBadge[]> {
  try {
    if (!authId) return [];

    // 1) Turn auth_id → internal users.id
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', authId)
      .single();

    if (userError || !userData) {
      console.error('Error fetching internal user ID by auth_id:', userError);
      return [];
    }
    const internalUserId = userData.id;

    // 2) Fetch all memberships for that internalUserId
    const { data: memberships, error: membershipsError } = await supabase
      .from('user_clubs')
      .select(`
        is_primary,
        clubs (
          club_id,
          club_name,
          logo
        )
      `)
      .eq('user_id', internalUserId);

    if (membershipsError) {
      console.error('Error fetching user_clubs for user_id:', membershipsError);
      return [];
    }

    // 3) Map into ClubBadge[]
    return (memberships || [])
      .filter((m) => m.clubs)
      .map((membership) => ({
        id:         membership.clubs.club_id,
        name:       membership.clubs.club_name,
        logo_url:   membership.clubs.logo || '',
        is_primary: membership.is_primary ?? false,
      }));
  } catch (err) {
    console.error('Unexpected error in getUserClubBadges:', err);
    return [];
  }
}

/**
 * Check if a club is verified based on its name
 */
export async function isClubVerified(clubId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('club_claims')
      .select('status')
      .eq('club_id', clubId)
      .eq('status', 'verified')
      .maybeSingle();

    if (error) {
      if ((error as any).code === 'PGRST116') {
        return false;
      }
      console.error('Error checking club verification status:', error);
      return false;
    }

    return data !== null;
  } catch (err) {
    console.error('Error checking club verification status:', err);
    return false;
  }
}

/**
 * Get all verified clubs (RPC)
 */
export async function getVerifiedClubs(): Promise<{ club_name: string; status: string }[]> {
  try {
    const { data, error } = await supabase.rpc('get_verified_clubs');

    if (error) {
      console.error('Error fetching verified clubs:', error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('Error fetching verified clubs:', err);
    return [];
  }
}
